import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { bookingsAPI } from '../../../api/bookings';
import {
  sanitiseCardField,
  validateCardForm,
  TEST_CARD,
} from '../../../utils/cardValidation';

/**
 * Stateful hook that drives the Payment page:
 *   - loads the booking
 *   - handles Pocket Pay payment initiation (redirect or fallback to card form)
 *   - drives the card-form state and submits the processed payment
 *
 * The page component reads booking + step flags from here and renders
 * stateless presentation components.
 */
export default function usePaymentFlow(bookingId) {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [cardDetails, setCardDetails] = useState({
    card_number: '',
    cardholder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
  });
  const [errors, setErrors] = useState({});

  const loadBooking = useCallback(async () => {
    try {
      const data = await bookingsAPI.getBooking(bookingId);
      setBooking(data);
    } catch (e) {
      toast.error('Failed to load booking');
    }
  }, [bookingId]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  const updateCardField = useCallback((field, value) => {
    setCardDetails((prev) => ({ ...prev, [field]: sanitiseCardField(field, value) }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const fillTestCard = useCallback(() => {
    setCardDetails({ ...TEST_CARD });
    toast.info('Test card details filled');
  }, []);

  const initiatePayment = useCallback(async () => {
    setProcessing(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/payment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = {
            detail: {
              error: 'Server Error',
              code: response.status,
              message: response.statusText || 'Payment initiation failed',
            },
          };
        }
        const errorDetail = errorData.detail || errorData;
        let errorMessage;
        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (errorDetail.message) {
          errorMessage = `${errorDetail.error || 'Payment Error'}\n\nError Code: ${errorDetail.code}\nMessage: ${errorDetail.message}`;
          if (errorDetail.details) {
            errorMessage += `\n\nDetails: ${JSON.stringify(errorDetail.details, null, 2)}`;
          }
        } else {
          errorMessage = 'Payment initiation failed. Please try again.';
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.status === 'payment_ready' && data.payment_url) {
        toast.success('Redirecting to secure payment page...', { duration: 2000 });
        setTimeout(() => { window.location.href = data.payment_url; }, 1500);
      } else if (data.status === 'initiated') {
        setPaymentData(data);
        setPaymentInitiated(true);
        toast.success('Payment initiated. Please enter your card details.');
      } else {
        throw new Error(data.message || 'Payment initiation failed');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      if (error.message && error.message.includes('Courts no longer available')) {
        toast.error(error.message, { duration: 6000 });
        setTimeout(() => navigate('/'), 3000);
      } else {
        toast.error(error.message || 'Failed to initiate payment', {
          duration: 10000,
          style: { whiteSpace: 'pre-wrap' },
        });
      }
    } finally {
      setProcessing(false);
    }
  }, [bookingId, navigate]);

  const processPayment = useCallback(async (e) => {
    e.preventDefault();
    const newErrors = validateCardForm(cardDetails);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setProcessing(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/payment/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          card_number: cardDetails.card_number.replace(/\s/g, ''),
          cardholder_name: cardDetails.cardholder_name,
          expiry_month: cardDetails.expiry_month,
          expiry_year: cardDetails.expiry_year,
          cvv: cardDetails.cvv,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Payment processing failed');
      }
      const data = await response.json();
      if (data.status === 'success') {
        toast.success('Payment successful!');
        navigate(`/booking-confirmed/${bookingId}`);
      } else {
        throw new Error(data.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(error.message || 'Payment processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [bookingId, cardDetails, navigate]);

  return {
    booking,
    processing,
    paymentInitiated,
    paymentData,
    cardDetails,
    errors,
    updateCardField,
    fillTestCard,
    initiatePayment,
    processPayment,
  };
}
